import { useCallback, useState } from "react";
import type { LatLngTuple } from "leaflet";
import { haversineDistanceMeters } from "../model/geo/reproject.ts";

/**
 * GeoreferencedMap.tsx's own "measure distance" tool: click to add a point,
 * right-click to clear the whole path and start over (AppToolbar.tsx's own
 * toggle button turns it on/off). Distances are always real great-circle
 * ones (haversine over the clicked lat/lng, same as `drawnConduitLength`'s
 * own geographic-CRS branch) — deliberately unit-agnostic (plain meters):
 * converting to the project's own length unit only happens at render time
 * (MeasurePanel.tsx, via `metersToProjectLength`), which is the only place
 * that actually needs `flowUnits`.
 */
export function useMeasureTool() {
  const [active, setActive] = useState(false);
  const [points, setPoints] = useState<LatLngTuple[]>([]);

  // Every activation (or deactivation) starts from a clean slate — turning
  // this back on after clicking something else in between shouldn't resume
  // a stale, half-forgotten path.
  const toggle = useCallback(() => {
    setActive((enabled) => !enabled);
    setPoints([]);
  }, []);

  const deactivate = useCallback(() => {
    setActive(false);
    setPoints([]);
  }, []);

  const addPoint = useCallback((latlng: LatLngTuple) => {
    setPoints((prev) => [...prev, latlng]);
  }, []);

  const clear = useCallback(() => setPoints([]), []);

  const segmentsMeters: number[] = [];
  for (let i = 1; i < points.length; i++) {
    const [lat1, lon1] = points[i - 1];
    const [lat2, lon2] = points[i];
    segmentsMeters.push(haversineDistanceMeters([lon1, lat1], [lon2, lat2]));
  }
  const totalMeters = segmentsMeters.reduce((sum, meters) => sum + meters, 0);

  return { active, points, segmentsMeters, totalMeters, toggle, deactivate, addPoint, clear };
}
